import { AfterContentChecked, Component, ElementRef, EventEmitter, Input, Output, TemplateRef, ViewChild } from "@angular/core";

import { UserHelper } from "../../../helpers/user.helper";
import { PwaService } from "../../../services/pwa/pwa.service";
import { getCaretCoordinates } from "./caret-coords";
import { getContentEditableCaretCoords, isInputOrTextAreaElement } from "./mention-utils";

/**
 * Angular Mentions.
 * https://github.com/dmacfarlane/angular-mentions
 *
 * Copyright (c) 2016 Dan MacFarlane
 */
@Component({
    selector: 'mention-list',
    templateUrl: './mention-list.component.html',
    styleUrls: ['./mention-list.component.scss'],
})
export class MentionListComponent implements AfterContentChecked {
    private coords: { top: number; left: number } = { top: 0, left: 0 };
    private offset: number = 0;

    public TRUNCATE_LIMIT_DESKTOP = TRUNCATE_LIMIT_DESKTOP;
    public TRUNCATE_LIMIT_MOBILE = TRUNCATE_LIMIT_MOBILE;
    public activeIndex: number = 0;
    @ViewChild('defaultItemTemplate', { static: true }) public defaultItemTemplate: TemplateRef<any>;
    public dropUp: boolean = false;
    public hidden: boolean = false;
    @Output() public itemClick = new EventEmitter();
    @Input() public itemTemplate: TemplateRef<any>;
    public items = new Array<any>();
    @Input() public labelKey: string = 'label';
    @ViewChild('list', { static: true }) public list: ElementRef;
    public styleOff: boolean = false;

    constructor(private element: ElementRef, public readonly userHelper: UserHelper, public readonly pwaService: PwaService) {}

    public get activeItem() {
        return this.items[this.activeIndex];
    }

    public activateNextItem() {
        // select the next item first
        const nextIndex = Math.min(this.activeIndex + 1, this.items.length - 1);
        this.activeIndex = nextIndex;

        // Direct scroll to the li element at this index
        this.scrollToLiByIndex(this.activeIndex);
    }

    public activatePreviousItem() {
        // select the previous item first
        const prevIndex = Math.max(this.activeIndex - 1, 0);
        this.activeIndex = prevIndex;

        // Direct scroll to the li element at this index
        this.scrollToLiByIndex(this.activeIndex);
    }

    public ngAfterContentChecked() {
        if (!this.itemTemplate) {
            this.itemTemplate = this.defaultItemTemplate;
        }
    }

    // lots of confusion here between relative coordinates and containers
    public position(nativeParentElement: HTMLInputElement, iframe: HTMLIFrameElement = null) {
        if (isInputOrTextAreaElement(nativeParentElement)) {
            // parent elements need to have postition:relative for this to work correctly?
            this.coords = getCaretCoordinates(nativeParentElement, nativeParentElement.selectionStart, null);
            this.coords.top = nativeParentElement.offsetTop + this.coords.top - nativeParentElement.scrollTop;
            this.coords.left = nativeParentElement.offsetLeft + this.coords.left - nativeParentElement.scrollLeft;

            // getCretCoordinates() for text/input elements needs an additional offset to position the list correctly
            this.offset = this.getBlockCursorDimensions(nativeParentElement).height;
        } else if (iframe) {
            let context: { iframe: HTMLIFrameElement; parent: Element } = {
                iframe: iframe,
                parent: iframe.offsetParent,
            };
            this.coords = getContentEditableCaretCoords(context);
        } else {
            let doc = document.documentElement;
            let scrollLeft = (window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0);
            let scrollTop = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
            // bounding rectangles are relative to view, offsets are relative to container?
            let caretRelativeToView = getContentEditableCaretCoords({ iframe: iframe });
            let parentRelativeToContainer: ClientRect = nativeParentElement.getBoundingClientRect();
            this.coords.top = caretRelativeToView.top - parentRelativeToContainer.top + nativeParentElement.offsetTop - scrollTop;
            this.coords.left = caretRelativeToView.left - parentRelativeToContainer.left + nativeParentElement.offsetLeft - scrollLeft;
        }
        // set the default/inital position
        this.positionElement();
    }

    // reset for a new mention search
    public reset() {
        this.list.nativeElement.scrollTop = 0;
        this.checkBounds();
    }

    // final positioning is done after the list is shown (and the height and width are known)
    // ensure it's in the page bounds
    private checkBounds() {
        let left = this.coords.left,
            top = this.coords.top,
            dropUp = this.dropUp;
        const bounds: ClientRect = this.list.nativeElement.getBoundingClientRect();
        // if off right of page, align right
        if (bounds.left + bounds.width > window.innerWidth) {
            left -= bounds.left + bounds.width - window.innerWidth + 10;
        }
        // if more than half off the bottom of the page, force dropUp
        // if ((bounds.top+bounds.height/2)>window.innerHeight) {
        //   dropUp = true;
        // }
        // if top is off page, disable dropUp
        if (bounds.top < 0) {
            dropUp = false;
        }
        // set the revised/final position
        this.positionElement(left, top, dropUp);
    }

    private getBlockCursorDimensions(nativeParentElement: HTMLInputElement) {
        const parentStyles = window.getComputedStyle(nativeParentElement);
        return {
            height: parseFloat(parentStyles.lineHeight),
            width: parseFloat(parentStyles.fontSize),
        };
    }

    private positionElement(left: number = this.coords.left, top: number = this.coords.top, dropUp: boolean = this.dropUp) {
        const el: HTMLElement = this.element.nativeElement;
        top += dropUp ? 0 : this.offset; // top of list is next line
        el.className = dropUp ? 'dropup' : null;
        el.style.position = 'absolute';
        el.style.left = left + 'px';
        el.style.top = top + 'px';
    }

    private scrollToLiByIndex(index: number) {
        let listEl: HTMLElement = this.list.nativeElement;
        // Get all li elements (skip the first one which is the title)
        const liElements = listEl.querySelectorAll('li');

        // Skip the first li (Membres title) and get the item at the specified index
        if (liElements.length > index + 1) {
            const targetLi = liElements[index + 1] as HTMLElement;
            if (targetLi) {
                // Scroll the target li into view
                targetLi.scrollIntoView({ block: 'nearest', behavior: 'auto' });
            }
        }
    }
}

const TRUNCATE_LIMIT_DESKTOP = 30;
const TRUNCATE_LIMIT_MOBILE = 25;
